// tab-calendar-multi.component.ts
import { Component, AfterViewInit, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, DateSelectArg, EventClickArg } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';

import { FormEntryComponent } from 'src/app/components/admin-components/hourglass/form-entry/form-entry.component';
import { GoogleService } from 'src/app/services/google.service';
import { HourglassService } from 'src/app/services/hourglass.service';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import * as weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend((utc as any).default || (utc as any));
dayjs.extend((timezone as any).default || (timezone as any));
dayjs.extend((weekOfYear as any).default || (weekOfYear as any));

const COLORS = {
  google:    { bg: '#2979ff', border: '#1e5fcc', text: '#ffffff' },
  hourglass: { bg: '#2e7d32', border: '#1b5e20', text: '#ffffff' }
};

@Component({
  selector: 'app-tab-calendar-multi',
  templateUrl: './tab-calendar-multi.component.html',
  styleUrls: []
})
export class TabCalendarMultiComponent implements AfterViewInit, OnDestroy {

  userId: any = sessionStorage.getItem('wOSImpersonate') || sessionStorage.getItem('wOSAuthUser');
  token = '';

  hourglassEvents: any[] = [];
  calendarEvents: any[] = [];
  private lastRangeKey = '';

  gridFilters = {
    timeWeekKey: [dayjs().year(), dayjs().week()].join('_'),
    timeWeek: dayjs().week(),
    timeYear: dayjs().year()
  };
  listTimeWeeks: any[] = [];
  showEntryModal = false;

  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  @ViewChild('ctlEntryCalendar') formEntry!: FormEntryComponent;

  private resizeObs?: ResizeObserver;
  private savedHooked = false;

  calendarOptions: CalendarOptions = {
    schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
    plugins: [resourceTimeGridPlugin, interactionPlugin],
    initialView: 'resourceTimeGridWeek',
    scrollTime: '07:30:00',
    selectable: true,
    unselectAuto: false,
    firstDay: 0,
    height: '75vh',
    headerToolbar: { left: 'title', center: '', right: 'prev,next' },
    datesAboveResources: true,
    views: {
      resourceTimeGridWeek: { type: 'resourceTimeGrid', duration: { weeks: 1 }, buttonText: 'Week' }
    },
    resources: [
      { id: 'google',    title: 'Calendar' },
      { id: 'hourglass', title: 'Hourglass' }
    ],
    nowIndicator: true,
    slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: true },
    dayHeaderFormat:  { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true },
    slotDuration: '00:30:00',
    snapDuration: '00:15:00',
    allDaySlot: true,
    expandRows: true,
    selectAllow: (arg: any) => {
      const r = (arg as any).resource;
      return !!r && r.id === 'hourglass';
    },
    eventMinHeight: 24,
    eventTimeFormat: { hour: 'numeric', minute: '2-digit', meridiem: true },
    eventDidMount: (info) => {
      const e = info.event;
      const loc = (e.extendedProps as any)?.location || '';
      info.el.title = [e.title, info.timeText, loc].filter(Boolean).join(' • ');
      Object.assign(info.el.style, {
        padding: '2px 4px',
        fontSize: '11px',
        lineHeight: '1.1',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      });
    },
    eventContent: (arg) => {
      const title = arg.event.title || '(No title)';
      const loc = (arg.event.extendedProps as any)?.location;
      return {
        html: `
          <div style="display:flex;flex-direction:column;gap:2px;">
            <div style="font-weight:600">${arg.timeText || ''}</div>
            <div style="font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
              ${title}${loc ? ` <span style="opacity:.7">— ${loc}</span>` : '' }
            </div>
          </div>`
      };
    },
    events: [],
    dateClick: () => {},
    eventClick: (info) => this.handleEventClick(info),
    select:     (sel)  => this.handleSelect(sel),
    datesSet:   (arg)  => this.refreshForVisibleRange(arg.start, arg.end)
  };

  constructor(
    private hourglassService: HourglassService,
    private googleService: GoogleService
  ) {}

  ngAfterViewInit() {
    const userStr = sessionStorage.getItem('wOSUser');
    this.token = userStr ? JSON.parse(userStr).stsTokenManager.accessToken : '';

    setTimeout(() => this.formEntry?.getUXData(this.userId, 'all'));

    this.attachFormEntryRefreshHooks();

    this.setupSizeGuards();
    this.loadWeekOptions().finally(() => this.ensureSizedAndFetchInitial());
  }

  ngOnDestroy(): void {
    if (this.resizeObs) this.resizeObs.disconnect();
  }

  private setupSizeGuards() {
    const hostEl =
      (this.calendarComponent as any)?.el?.nativeElement ||
      (this.calendarComponent as any)?.element?.nativeElement ||
      null;

    const bump = () => this.calendarComponent?.getApi()?.updateSize();

    requestAnimationFrame(bump);
    setTimeout(bump, 50);
    setTimeout(bump, 150);
    setTimeout(bump, 300);

    if (hostEl && 'ResizeObserver' in window) {
      this.resizeObs = new ResizeObserver(() => bump());
      this.resizeObs.observe(hostEl);
    }
  }

  private ensureSizedAndFetchInitial() {
    const api = this.calendarComponent?.getApi();
    if (!api) return;

    api.render();
    api.updateSize();

    setTimeout(() => {
      if (api.view) this.refreshForVisibleRange(api.view.activeStart, api.view.activeEnd, true);
    }, 0);
  }

  private attachFormEntryRefreshHooks() {
    if (this.savedHooked) return;

    const fe: any = this.formEntry;
    if (!fe) return;

    if (typeof fe.saveEntry === 'function' && !fe.__wrapped_saveEntry) {
      const original = fe.saveEntry.bind(fe);

      fe.saveEntry = async (...args: any[]) => {
        const result = await original(...args);
        this.refreshVisibleRangeNow();
        setTimeout(() => this.refreshVisibleRangeNow(), 300);
        this.closeEntryModal();

        return result;
      };

      fe.__wrapped_saveEntry = true;
      this.savedHooked = true;
      console.log('[TabCalendarMulti] wrapped saveEntry');
    }
  }

  private refreshVisibleRangeNow() {
    const api = this.calendarComponent?.getApi();
    if (api?.view) this.refreshForVisibleRange(api.view.activeStart, api.view.activeEnd, true);
  }

  openEntryModal(): void {
    this.showEntryModal = true;
  }

  closeEntryModal(): void {
    this.showEntryModal = false;
    this.clearSelectionHighlight();
  }

  onModalBackdropClick(_evt: MouseEvent): void {
    this.closeEntryModal();
  }

  private clearSelectionHighlight(): void {
    const api = this.calendarComponent?.getApi();
    if (api) {
      api.unselect();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    const api = this.calendarComponent?.getApi();
    if (api) api.updateSize();
  }

  onTabShown() {
    const api = this.calendarComponent?.getApi();
    if (!api) return;
    api.updateSize();
    api.render();
    if (api.view) {
      this.syncWeekSelectorWithView(api.view.activeStart);
      this.refreshForVisibleRange(api.view.activeStart, api.view.activeEnd, true);
    }
  }

  private syncWeekSelectorWithView(rangeStart: Date) {
    const yr = dayjs(rangeStart).year();
    const wk = dayjs(rangeStart).week();
    const id = `${yr}_${wk}`;

    let match = this.listTimeWeeks?.find((w: any) => w?.id === id);
    if (!match) {
      const startIso = dayjs(rangeStart).format('YYYY-MM-DD');
      match = this.listTimeWeeks?.find((w: any) =>
        !!w?.wkstart &&
        dayjs(w.wkstart, ['YYYY-MM-DD','YYYY/MM/DD','MM/DD/YYYY','M/D/YYYY'], true)
          .format('YYYY-MM-DD') === startIso
      );
    }
    this.gridFilters.timeYear = yr;
    this.gridFilters.timeWeek = wk;
    this.gridFilters.timeWeekKey = match?.id ?? id;
  }

  onWeekSelectChange(selectedId?: string) {
    try {
      const id = selectedId ?? this.gridFilters.timeWeekKey;
      if (!id) return;
      const [yr, wk] = id.split('_').map((n) => Number(n));
      if (!yr || !wk) return;

      this.gridFilters.timeYear = yr;
      this.gridFilters.timeWeek = wk;

      const w = this.listTimeWeeks.find((x: any) => x.id === id);
      const targetDate =
        (w && this.toDate(w.wkstart)) || this.computeWeekStart(yr, wk);

      const api = this.calendarComponent?.getApi();
      if (!api || !targetDate) return;

      this.lastRangeKey = '';
      api.gotoDate(targetDate);

      if (api.view) this.refreshForVisibleRange(api.view.activeStart, api.view.activeEnd, true);
    } catch { /* noop */ }
  }

  private toDate(val: string | Date | undefined): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    const d = dayjs(val, ['YYYY-MM-DD','YYYY/MM/DD','MM/DD/YYYY','M/D/YYYY'], true);
    return d.isValid() ? d.toDate() : null;
  }
  private computeWeekStart(year: number, week: number): Date {
    return dayjs().year(year).week(week).startOf('week').toDate();
  }

  private async loadWeekOptions() {
    const userId =
      sessionStorage.getItem('wOSImpersonate') ||
      sessionStorage.getItem('wOSAuthUser') || '';
    if (!userId) { this.listTimeWeeks = []; return; }

    const wk = dayjs().week();
    const yr = dayjs().year();
    const filters = { timeWeekKey: `${yr}_${wk}`, timeWeek: wk, timeYear: yr };

    try {
      const data: any = await this.hourglassService.getTimeEntryUxData(userId, 'all', filters, this.token);
      this.listTimeWeeks = data?.timeweeks || [];
      if (this.listTimeWeeks.length && !this.gridFilters.timeWeekKey) {
        this.gridFilters.timeWeekKey = this.listTimeWeeks[0].id;
      }
    } catch {
      this.listTimeWeeks = [];
    }
  }

  async refreshForVisibleRange(rangeStart: Date, rangeEnd: Date, force = false) {
    this.syncWeekSelectorWithView(rangeStart);

    const startISO = this.toISOStart(rangeStart);
    const endISO   = this.toISOEnd(rangeEnd);

    const rangeKey = `${dayjs(rangeStart).format('YYYY-MM-DD')}_${dayjs(rangeEnd).format('YYYY-MM-DD')}`;
    if (!force && rangeKey === this.lastRangeKey) return;
    this.lastRangeKey = rangeKey;

    await Promise.all([
      this.loadHourglassEvents(startISO.substring(0, 10)),
      this.loadGoogleCalendarEvents(startISO, endISO)
    ]);

    this.updateCalendarEvents();
    setTimeout(() => this.calendarComponent?.getApi()?.updateSize(), 0);
  }

  private toISOStart(d: Date): string {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return dayjs(d).startOf('day').tz(tz).utc().format();
  }
  private toISOEnd(d: Date): string {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return dayjs(d).endOf('day').tz(tz).utc().format();
  }

  async loadGoogleCalendarEvents(startISO?: string, endISO?: string) {
    this.calendarEvents = [];
    return new Promise<void>((resolve) => {
      this.googleService.getCalendarEventsWeekly(startISO, endISO, this.token)
        .subscribe({
          next: (data: any) => {
            const items: any[] = Array.isArray(data?.items) ? data.items : [];

            const hasInstances = new Set<string>();
            for (const it of items) if (it?.recurringEventId) hasInstances.add(it.recurringEventId);

            const mapped: any[] = [];
            for (const obj of items) {
              if (!obj || obj.status === 'cancelled') continue;

              const isMaster = Array.isArray(obj.recurrence) && obj.recurrence.length > 0;
              if (isMaster && hasInstances.has(obj.id)) continue;

              const start =
                obj.start?.dateTime || (obj.start?.date ? obj.start.date + 'T00:00:00Z' : null);
              const end =
                obj.end?.dateTime || (obj.end?.date ? obj.end.date + 'T23:59:59Z' : null);
              if (!start || !end) continue;

              mapped.push({
                id: `google-${obj.id}`,
                title: obj.summary || '(No title)',
                start, end,
                allDay: !!obj.start?.date,
                resourceId: 'google',
                backgroundColor: COLORS.google.bg,
                borderColor: COLORS.google.border,
                textColor: COLORS.google.text,
                extendedProps: { source: 'google', location: obj.location || '' },
                classNames: ['p-1']
              });
            }

            const seen = new Set<string>();
            for (const ev of mapped) {
              const k = `${ev.id}|${new Date(ev.start).toISOString()}|${new Date(ev.end).toISOString()}`;
              if (!seen.has(k)) { seen.add(k); this.calendarEvents.push(ev); }
            }
            resolve();
          },
          error: () => resolve()
        });
    });
  }

  async loadHourglassEvents(startDateISO?: string) {
    const userId =
      sessionStorage.getItem('wOSImpersonate') ||
      sessionStorage.getItem('wOSAuthUser') || '';
    if (!userId) { this.hourglassEvents = []; return; }

    const start = dayjs(startDateISO || dayjs().toISOString());
    const wk = start.week();
    const yr = start.year();
    const timeWeekKey = [yr, wk].join('_');

    const filters: any = {
      timeWeekKey, timeWeek: wk, timeYear: yr,
      mode: 'entries',
      entry_owner: Number(userId)
    };

    try {
      const data: any = await this.hourglassService.getTimeEntryUxData(userId, 'entries', filters, this.token);
      const entries = Array.isArray(data) ? data : data?.entries || [];

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      this.hourglassEvents = entries
        .map((entry: any, idx: number) => {
          const startUtc = entry.t_start;
          const endUtc = entry.t_end;
          if (!startUtc || !endUtc) return null;

          const s = dayjs.utc(startUtc).tz(tz);
          const e = dayjs.utc(endUtc).tz(tz);
          if (!s.isValid() || !e.isValid()) return null;

          return {
            id: `hourglass-${entry.id ?? idx}`,
            title: entry.t_name || entry.task_name || 'Untitled',
            start: s.toDate(),
            end: e.toDate(),
            allDay: false,
            resourceId: 'hourglass',
            backgroundColor: COLORS.hourglass.bg,
            borderColor: COLORS.hourglass.border,
            textColor: COLORS.hourglass.text,
            extendedProps: {
              source: 'hourglass',
              orig: entry,
              billable: entry.billable,
              notes: entry.notes,
              assignees: entry.assignees,
              assignee_names: entry.assignee_names,
              entry_owner: entry.entry_owner,
              entry_type: entry.entry_type,
              task_id: entry.task_id
            },
            classNames: ['p-1']
          };
        })
        .filter(Boolean as any);
    } catch {
      this.hourglassEvents = [];
    }
  }

  public updateCalendarEvents() {
    const all = [...this.hourglassEvents, ...this.calendarEvents];

    const seen = new Set<string>();
    const deduped: any[] = [];
    for (const ev of all) {
      const k = `${ev.id}|${new Date(ev.start).toISOString()}|${new Date(ev.end).toISOString()}|${ev.resourceId || ''}`;
      if (!seen.has(k)) { seen.add(k); deduped.push(ev); }
    }

    const api = this.calendarComponent?.getApi();
    if (!api) return;

    api.removeAllEvents();
    for (const ev of deduped) api.addEvent(ev);
    api.render();
  }

  private setDurationFromRange(start: Date, end: Date): void {
    const mins = Math.max(1, dayjs(end).diff(dayjs(start), 'minute'));
    this.formEntry.entryForm.entryDurationHrs = Math.floor(mins / 60);
    this.formEntry.entryForm.entryDurationMins = mins % 60;
    (this.formEntry.entryForm as any).entryDuration = mins;
    if (Object.prototype.hasOwnProperty.call(this.formEntry.entryForm as any, 'entryDurationLabel')) {
      (this.formEntry.entryForm as any).entryDurationLabel =
        mins < 60 ? `${mins} minutes` : `${mins / 60} hours`;
    }
  }

  private async handleEventClick(arg: EventClickArg): Promise<void> {
    const resId =
      (arg.event as any)?._def?.resourceIds?.[0] ||
      (arg.event as any)?.extendedProps?.resourceId || '';

    if (resId === 'google') {
      const summary = arg?.event?.title ?? '';
      const start   = arg?.event?.start;
      const end     = arg?.event?.end;
      if (!start || !end) return;

      let taskId = 0;
      try {
        taskId = Number(await this.hourglassService.lookupTask(summary, this.userId, this.token)) || 0;
      } catch { taskId = 0; }

      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const s = dayjs.utc(start).tz(zone);
      const e = dayjs.utc(end).tz(zone);

      this.formEntry.resetEntry();
      this.formEntry.setEntryType(summary);
      this.formEntry.entryForm.entryDt    = s.format('YYYY-MM-DD');
      this.formEntry.entryForm.entryStart = s.format('HH:mm');
      this.formEntry.entryForm.entryEnd   = e.format('HH:mm');
      this.setDurationFromRange(s.toDate(), e.toDate());

      if (taskId > 0) {
        this.formEntry.entryForm.taskID = taskId;
        setTimeout(() => this.formEntry.setTaskFilter(), 0);
      } else {
        this.formEntry.entryForm.taskID = '';
        this.formEntry.entryForm.taskObject = [];
      }

      this.openEntryModal();
      return;
    }

    if (resId === 'hourglass') {
      const ev = arg.event;
      const xp: any = ev.extendedProps || {};
      const orig: any = xp.orig || {};

      const durationMin = Math.max(1, dayjs(ev.end!).diff(dayjs(ev.start!), 'minute'));

      const dataset = {
        id: (orig.id ?? String(ev.id).replace('hourglass-', '')) + '',
        entry_owner: (orig.entry_owner ?? this.userId) + '',
        entry_type: orig.entry_type ?? '',
        task_id: (orig.task_id ?? '') + '',
        t_date: dayjs(ev.start!).format('YYYY-MM-DD'),
        t_start: dayjs(ev.start!).utc().format(),
        notes: orig.notes ?? '',
        billable: orig.billable ?? 0,
        assignees: orig.assignees ?? '',
        assignee_names: orig.assignee_names ?? '',
        duration: durationMin
      };

      this.formEntry.loadFromGrid(dataset);

      this.openEntryModal();
      return;
    }
  }

  private handleSelect(sel: DateSelectArg): void {
    const resource: any = (sel as any).resource;
    if (!resource || resource.id !== 'hourglass') return;

    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const s = dayjs.utc(sel.start).tz(zone);
    const e = dayjs.utc(sel.end).tz(zone);

    this.formEntry.resetEntry();
    this.formEntry.entryForm.entryDt    = s.format('YYYY-MM-DD');
    this.formEntry.entryForm.entryStart = s.format('HH:mm');
    this.formEntry.entryForm.entryEnd   = e.format('HH:mm');
    this.setDurationFromRange(s.toDate(), e.toDate());

    this.openEntryModal();
  }

  getEventsSync(): void {
    const api = this.calendarComponent?.getApi();
    if (api?.view) this.refreshForVisibleRange(api.view.activeStart, api.view.activeEnd, true);
  }

  trackByFn(_index: number, record: any) { return record?.id || _index; }
}
